import Button from "../../../componentLibrary/Button";
import Checkbox from "../../../componentLibrary/Checkbox";
import Input from "../../../componentLibrary/Input";
import Modal from "../../../componentLibrary/Modal";
import Spinner from "../../../componentLibrary/Spinner";
import Textarea from "../../../componentLibrary/Textarea";
import { Permission } from "../../../types/rbac";

interface RoleFormData {
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
}

interface RoleFormModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  formData: RoleFormData;
  groupedPermissions: Record<string, Permission[]>;
  isLoading: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onFormChange: (data: Partial<RoleFormData>) => void;
  onTogglePermission: (permissionId: string) => void;
}

const RoleFormModal = ({
  isOpen,
  mode,
  formData,
  groupedPermissions,
  isLoading,
  isSubmitting,
  onClose,
  onSubmit,
  onFormChange,
  onTogglePermission,
}: RoleFormModalProps) => {
  const title = mode === "create" ? "Create New Role" : "Edit Role";
  const submitLabel = mode === "create" ? "Create Role" : "Update Role";
  const submittingLabel = mode === "create" ? "Creating..." : "Updating...";

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={onClose}
      title={title}
      size="xl"
      footer={
        <div className="flex gap-2">
          <Button onClick={onSubmit} disabled={isSubmitting} className="flex-1">
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-semibold mb-1">
            Internal Name {mode === "create" && "*"}
          </label>
          <Input
            type="text"
            placeholder="e.g., content_manager"
            value={formData.name}
            onChange={(e) => onFormChange({ name: e.target.value })}
            disabled={mode === "edit"}
          />
          <p className="text-xs text-gray-500 mt-1">
            {mode === "create"
              ? "Used in code. Cannot be changed later."
              : "Cannot be changed"}
          </p>
        </div>

        <div className="flex flex-col gap-1 mt-4">
          <label className="block text-sm font-semibold mb-1">
            Display Name *
          </label>
          <Input
            type="text"
            placeholder="e.g., Content Manager"
            value={formData.displayName}
            onChange={(e) => onFormChange({ displayName: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1 mt-4">
          <label className="block text-sm font-semibold mb-1">
            Description
          </label>
          <Textarea
            placeholder="Describe what this role does..."
            value={formData.description}
            onChange={(e) => onFormChange({ description: e.target.value })}
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="block text-sm font-semibold mb-2">
            Permissions
          </label>
          {isLoading ? (
            <Spinner />
          ) : (
            <div className="space-y-4 pr-2">
              {Object.entries(groupedPermissions).map(([group, perms]) => (
                <div key={group}>
                  <div className="font-semibold text-sm text-gray-700 mb-2">
                    {group}
                  </div>
                  <div className="space-y-2 pl-2">
                    {perms.map((perm) => (
                      <Checkbox
                        key={perm.id}
                        label={`${perm.id} - ${perm.description}`}
                        checked={formData.permissions.includes(perm.id)}
                        onChange={() => onTogglePermission(perm.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RoleFormModal;
